const EXTENSION_FRAME = /(chrome|moz|safari-web)-extension:\/\//;
const THIRD_PARTY_SCRIPT_FRAME =
  /https?:\/\/[^/\s]*(hscollectedforms\.net|hsadspixel\.net|hs-scripts\.com|hs-analytics\.net|i\.posthog\.com)\//;
const NATIVE_FRAME = /\(<anonymous>\)|\[native code\]/;
const NETWORK_FAILURE = /^(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.?)$/;

/**
 * True when the error is not ours to fix.
 *
 * Extensions inject scripts into our pages, and their rejections land in our
 * `unhandledrejection` handler as if they were ours. One extension's
 * `reading 'M_ID'` bug produced more prod issues than all of apps/web combined.
 * A stack with even one first-party frame is kept — that is our code being
 * called by, or calling into, the extension, and could be our bug.
 *
 * The one exception is a network failure thrown from an extension that wraps
 * `window.fetch`: every caller on the page (PostHog, Clerk, our exporter) then
 * rejects from inside the extension. Our own failed requests are already on the
 * fetch/XHR spans, so the rejection adds nothing.
 */
export function isForeignExtensionError(error: unknown): boolean {
  const stack = error instanceof Error ? error.stack : undefined;
  if (!stack) return false;

  const frames = stack
    .split('\n')
    .filter((line) => /^\s*at\s|@/.test(line))
    .filter((line) => !NATIVE_FRAME.test(line));
  if (frames.length === 0) return false;

  if (frames.every((frame) => EXTENSION_FRAME.test(frame) || THIRD_PARTY_SCRIPT_FRAME.test(frame))) return true;

  return error instanceof TypeError && NETWORK_FAILURE.test(error.message) && EXTENSION_FRAME.test(frames[0]);
}
